
import React from "react";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Map, Eye, Lock, PieChart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { usePlatform } from "@/hooks/usePlatform";

const HeroSection = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isNative } = usePlatform();

  const handleGetStarted = () => {
    navigate("/login");
  };

  const scrollToHowItWorks = () => {
    document.getElementById('how-it-works')?.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'start' 
    });
  };

  return (
    <div className="bg-black text-white">
      <div className="container py-20 md:py-32">
        <div className="mx-auto max-w-3xl text-center">
          <h1 className="mb-6 text-4xl font-bold leading-tight md:text-5xl lg:text-6xl">
            Report Crimes.
            <br />
            Create Safer Communities.
          </h1>
          <p className="mb-10 text-lg text-gray-300 md:text-xl">
            Okkio empowers you to securely report crimes in your area, 
            helping law enforcement create safer neighborhoods for everyone.
          </p>
          <div className="flex flex-col items-center space-y-6">
            <div className={`flex ${isNative ? 'flex-col' : 'flex-wrap'} justify-center gap-4`}>
              <Button 
                onClick={handleGetStarted}
                className={`bg-okkio-orange px-6 py-3 text-base ${isNative ? 'w-full' : ''}`}
              >
                Get Started <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              
              <Link to="/report-crime" className={isNative ? "w-full" : ""}>
                <Button 
                  className={`bg-okkio-orange px-6 py-3 text-base ${isNative ? 'w-full' : ''}`}
                >
                  Report <Eye className="ml-2 h-4 w-4" />
                </Button>
              </Link>

              <Link to="/crime-map" className={isNative ? "w-full" : ""}>
                <Button 
                  className={`bg-okkio-orange px-6 py-3 text-base ${isNative ? 'w-full' : ''}`}
                >
                  Around You <Map className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            
            <Button 
              onClick={scrollToHowItWorks} 
              variant="outline" 
              className={`px-6 py-2 text-sm bg-white text-black border-white hover:bg-gray-100 ${isNative ? 'w-full' : 'w-auto'}`}
            >
              Learn More
            </Button>
          </div>
        </div>
      </div>
      <div className="bg-gradient-to-b from-black/50 to-white py-16">
        <div className="container">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-3">
            {[
              {
                icon: Eye,
                title: "Anonymous Reporting",
                description: "Report crimes anonymously without fear of retaliation."
              },
              {
                icon: Lock,
                title: "Secure & Protected",
                description: "Military-grade encryption and strict privacy controls."
              },
              {
                icon: PieChart,
                title: "Impact Dashboard",
                description: "Track how your reports help make communities safer."
              }
            ].map(({ icon: Icon, title, description }, index) => (
              <div key={index} className="rounded-lg bg-gray-900 p-6 shadow-lg">
                <div className="mb-4 rounded-full bg-gray-800 p-3 w-fit">
                  <Icon className="h-6 w-6 text-okkio-orange" />
                </div>
                <h3 className="mb-2 text-xl font-bold text-white">{title}</h3>
                <p className="text-gray-400">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;
